import { cn } from "@/primitives/utils"

/*
 * The marks the web app draws. Beacon's is the production Slack app's icon, the same file the
 * favicon uses (`public/beacon.svg`), so the web app and the bot look like one thing. Google's and
 * Slack's keep their own four colours, as their sign-in branding requires: Google's is shown only
 * where a Member signs in with Google or connects it, Slack's only where they sign in with Slack.
 */

const tiles = {
  20: "size-5 rounded-md",
  24: "size-6 rounded-md",
  32: "size-8 rounded-lg",
  40: "size-10 rounded-lg",
} as const

/**
 * Beacon's mark: a lit lighthouse on a navy tile, as in Slack. It is decoration beside the word
 * "Beacon", so a screen reader skips it.
 */
export function BeaconMark({ size }: { size: 20 | 24 | 32 | 40 }) {
  return <img src="/beacon.svg" alt="" aria-hidden className={cn("shrink-0", tiles[size])} />
}

const googleSizes = { 16: "size-4", 20: "size-5" } as const

/** Google's four-colour G, beside the words "Google" or "Continue with Google". */
export function GoogleMark({ size }: { size: 16 | 20 }) {
  return (
    <svg viewBox="0 0 48 48" aria-hidden className={cn("shrink-0", googleSizes[size])}>
      <path
        className="fill-mark-google-red"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        className="fill-mark-google-blue"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        className="fill-mark-google-yellow"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        className="fill-mark-google-green"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  )
}

const slackSizes = { 16: "size-4", 20: "size-5" } as const

/** Slack's four-colour mark, beside the words "Continue with Slack". */
export function SlackMark({ size }: { size: 16 | 20 }) {
  return (
    <svg viewBox="0 0 54 54" aria-hidden className={cn("shrink-0", slackSizes[size])}>
      <path
        className="fill-mark-slack-blue"
        d="M19.712.133a5.381 5.381 0 0 0-5.376 5.387 5.381 5.381 0 0 0 5.376 5.386h5.376V5.52A5.381 5.381 0 0 0 19.712.133m0 14.365H5.376A5.381 5.381 0 0 0 0 19.884a5.381 5.381 0 0 0 5.376 5.387h14.336a5.381 5.381 0 0 0 5.376-5.387 5.381 5.381 0 0 0-5.376-5.386"
      />
      <path
        className="fill-mark-slack-green"
        d="M53.76 19.884a5.381 5.381 0 0 0-5.376-5.386 5.381 5.381 0 0 0-5.376 5.386v5.387h5.376a5.381 5.381 0 0 0 5.376-5.387m-14.336 0V5.52A5.381 5.381 0 0 0 34.048.133a5.381 5.381 0 0 0-5.376 5.387v14.364a5.381 5.381 0 0 0 5.376 5.387 5.381 5.381 0 0 0 5.376-5.387"
      />
      <path
        className="fill-mark-slack-yellow"
        d="M34.048 54a5.381 5.381 0 0 0 5.376-5.387 5.381 5.381 0 0 0-5.376-5.386h-5.376v5.386A5.381 5.381 0 0 0 34.048 54m0-14.365h14.336a5.381 5.381 0 0 0 5.376-5.386 5.381 5.381 0 0 0-5.376-5.387H34.048a5.381 5.381 0 0 0-5.376 5.387 5.381 5.381 0 0 0 5.376 5.386"
      />
      <path
        className="fill-mark-slack-red"
        d="M0 34.249a5.381 5.381 0 0 0 5.376 5.386 5.381 5.381 0 0 0 5.376-5.386v-5.387H5.376A5.381 5.381 0 0 0 0 34.249m14.336 0v14.364A5.381 5.381 0 0 0 19.712 54a5.381 5.381 0 0 0 5.376-5.387V34.249a5.381 5.381 0 0 0-5.376-5.387 5.381 5.381 0 0 0-5.376 5.387"
      />
    </svg>
  )
}
